import { PureComponent, ReactNode } from 'react';

import { fetchMatchingSymDia } from '../../../api';
import { LOCAL_ID_PREFIX } from '../../../constants';
import { ONE_CHAR_GENDER } from '../../../pages/NewPatient';
import { connect } from 'react-redux';
import { ExaminationEntity } from '../../../store/types/prescription';
import { getPatientPersonalProfile } from '../../../store/selectors/prescriptionPad';
import { GlobalState } from '../../../store/reducers';
import PadV2AutoSuggest, {
    AllOptions,
    AS_OPTIONS_FREQUENCTLY,
    AS_OPTIONS_SEARCH,
} from '../rightPanel/workspace/padV2/PadV2AutoSuggest';
import { SymDiaResponse } from '../../../api/fetchMatchingSymDia';
import { randomString } from '../../../store/utils';
import { MedicalHistoryIcon } from '../../common/Icons';
import { PatientPersonalProfile } from '../../../api/pushVisit';
import PadGenieAutoSuggestFuseManager, { EXAMINATIONS } from '../PadGenieAutoSuggestFuseManager';

const EXAMINATION_FINDINGS_AS_EL_ID = 'EXAMINATION_FINDINGS_AS_EL_ID';
export const EXAMINATION_FINDINGS_V2_AS_INPUT = 'EXAMINATION_FINDINGS_V2_AS_INPUT';

type Props = {
    onSelect: (option: ExaminationEntity) => void;
    elements: ReadonlyArray<ExaminationEntity>;
    patientProfile?: PatientPersonalProfile;
};

const fuseConfig = { keys: ['name'], threshold: 0.2 };

type State = {
    inputValue: string;
};

class ExaminationSearchInput extends PureComponent<Props, State> {
    MedicalHistorySearchIcon = MedicalHistoryIcon(
        'fill-darwin-accent-examination-findings-sky-blue-900',
    );

    state: State = {
        inputValue: '',
    };

    getOptions = (
        setOptions: (options: AllOptions<ExaminationEntity>, inputValue?: string) => void,
        input?: string,
    ): void => {
        const options: AllOptions<ExaminationEntity> = [];
        const { patientProfile } = this.props;
        const { elements } = this.props;
        const fuse = PadGenieAutoSuggestFuseManager.getFuseForType(EXAMINATIONS);
        const gElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(EXAMINATIONS);

        if (!fuse) return;

        if (!input) {
            const genieOptions =
                gElements?.filter(Boolean)?.filter((e) => !elements.find((t) => t.id === e.id)) ||
                [];

            genieOptions.splice(7);

            options.push({
                label: AS_OPTIONS_FREQUENCTLY,
                options: genieOptions,
            });

            setOptions(options, input);
        } else {
            const filteredGItems = fuse
                .search(input)
                .map((option) => option.item)
                .filter(Boolean)
                .filter((e) => !elements.find((t) => t.id === e.id));

            filteredGItems.splice(7);

            const currentOptions = [
                { label: AS_OPTIONS_SEARCH as typeof AS_OPTIONS_SEARCH, options: filteredGItems },
            ];

            setOptions(currentOptions, input);

            fetchMatchingSymDia(
                input,
                patientProfile?.gender
                    ? ONE_CHAR_GENDER[patientProfile.gender.toLowerCase()]
                    : undefined,
            )
                .then((response) => {
                    this.handleApiOptions(currentOptions, input, setOptions, response);
                })
                .catch(() => {
                    this.handleApiOptions(currentOptions, input, setOptions);
                });
        }
    };

    handleApiOptions = (
        currentOptions: AllOptions<ExaminationEntity>,
        input: string,
        setOptions: (options: AllOptions<ExaminationEntity>, inputValue?: string) => void,
        response?: SymDiaResponse,
    ): void => {
        const { elements } = this.props;
        const allOptions = [...currentOptions[0].options];

        if (response?.symptoms) {
            response?.symptoms.forEach((t) => {
                if (!allOptions.find((i) => i.id === t.id)) allOptions.push(t);
            });
        }

        if (response?.diagnoses) {
            allOptions.push(...response.diagnoses);
        }

        const finalResults = allOptions?.filter((e) => !elements.find((t) => t.id === e.id));
        finalResults.splice(7);

        const isMatchFound = finalResults?.find(
            (i) => i.name.toLowerCase() === input.toLowerCase(),
        );

        if (!isMatchFound && input) {
            finalResults.push({
                id: `${LOCAL_ID_PREFIX}${new Date().getTime()}-${randomString(4)}`,
                name: input,
            });
        }

        setOptions([{ label: AS_OPTIONS_SEARCH, options: finalResults }], input);
    };

    render(): ReactNode {
        return (
            <PadV2AutoSuggest
                inputId={EXAMINATION_FINDINGS_V2_AS_INPUT}
                elId={EXAMINATION_FINDINGS_AS_EL_ID}
                onSelect={(data) => this.props.onSelect(data)}
                optionIconText={this.MedicalHistorySearchIcon}
                optionIconBgClass="circle-examination-findings"
                menuListClassName="examination-findings-dropdown"
                optionWrapperClass="examination-findings-dropdown-item"
                getOptions={this.getOptions}
                placeholder="Start typing findings..."
                elements={this.props.elements}
            />
        );
    }
}

export default connect((state: GlobalState) => ({
    patientProfile: getPatientPersonalProfile(state),
}))(ExaminationSearchInput);
